import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Round 26: bumped from default (no timeout) to 45s to absorb Emergent
// cold-start latency. Backend has a 13-migration boot chain that can
// exceed 30s on a cold container with grown data. 45s is the ceiling
// before UX feels broken anyway — if we hit it, the proper fix is
// gating cursor-iteration migrations (roadmap R25-D).
const DEFAULT_TIMEOUT_MS = 45000;
const RETRY_DELAY_MS = 1500;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
});

// Attach stored token as Authorization fallback (in addition to httpOnly cookie)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("aampl_npd_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Round 26: single retry on cold-start timeouts / network errors.
// - Retries ONLY on ECONNABORTED (axios timeout) or missing response
//   (network unreachable). 4xx / 5xx are NOT retried — those are real
//   errors and retrying would mask bugs.
// - Skips multipart uploads (drawings) — half-uploaded retries waste
//   bandwidth and risk double-creates.
// - _retryCount flag on the config prevents infinite loops if the
//   retry itself times out.
function _shouldRetry(err) {
  const cfg = err?.config;
  if (!cfg) return false;
  if (cfg._retryCount && cfg._retryCount >= 1) return false;
  const contentType =
    (cfg.headers && (cfg.headers["Content-Type"] || cfg.headers["content-type"])) || "";
  if (typeof contentType === "string" && contentType.includes("multipart/form-data")) return false;
  // axios timeout
  if (err.code === "ECONNABORTED") return true;
  // network error (no response received)
  if (!err.response && err.message && err.message.toLowerCase().includes("network")) return true;
  // generic no-response (DNS, connection refused during cold start)
  if (!err.response && !err.code) return true;
  return false;
}

function _sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Response interceptor: 401 redirect logic (preserved from earlier rounds)
// + Round 26 cold-start retry.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // Cold-start retry path — try once before any other handling.
    if (_shouldRetry(err)) {
      const cfg = err.config;
      cfg._retryCount = (cfg._retryCount || 0) + 1;
      await _sleep(RETRY_DELAY_MS);
      try {
        return await api.request(cfg);
      } catch (retryErr) {
        // fall through to the normal error pipeline with the retry's error
        err = retryErr;
      }
    }

    // Existing Round 27: global 401 → redirect to /login, exempting
    // /auth/me and /auth/login so their 401s don't race-wipe a concurrent login.
    if (err?.response?.status === 401) {
      const url = err?.config?.url || "";
      const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login");
      if (!isAuthProbe) {
        localStorage.removeItem("aampl_npd_token");
        localStorage.removeItem("aampl_npd_user");
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  },
);

export default api;
